import type { ScheduleActionType, SchedulingType } from '@frontend/api-ai-settings';

export type CallerIntent =
  | 'REPRESENTATIVE'
  | 'EMERGENCY'
  | 'BOOKING'
  | 'RESCHEDULE'
  | 'CANCEL'
  | 'CONFIRM'
  | 'GENERAL_INQUIRY'
  | 'UNKNOWN';

export type RoutingEventType = 'route' | 'endCall' | 'fatal' | 'textHandoff';

export interface StreamAudioMessage {
  type: 'streamAudio';
  data: {
    audioData: string;
    audioDataType: 'raw';
    sampleRate: number;
  };
}

export interface RouteActionMessage {
  type: RoutingEventType;
  data: {
    reason: string;
    caller_intent: CallerIntent;
  };
}

export interface TranscriptMessage {
  transcript: {
    role: 'user' | 'assistant';
    text: string;
  };
}

export type ScheduleActionStatus = 'SUCCEEDED' | 'FAILED' | 'UNKNOWN';

export type PatientType = 'EXISTING' | 'NEW' | 'UNKNOWN';

type AirClientScheduleActionBase = {
  action_status: ScheduleActionStatus;
  patient_type: PatientType;
  timezone: string;
};

type WeaveDirectScheduleAction = AirClientScheduleActionBase & {
  action_type: Extract<ScheduleActionType, 'SCHEDULE' | 'RESCHEDULE' | 'CANCEL' | 'CONFIRM'>;
  scheduling_type: Extract<SchedulingType, 'WEAVE_DIRECT_SCHEDULING'>;
  appointment_id: string;
  start_time: string;
};

type WeaveAppointmentRequestAction = AirClientScheduleActionBase & {
  action_type: ScheduleActionType;
  scheduling_type: Extract<SchedulingType, 'WEAVE_REQUEST_SCHEDULING'>;
  booking_submission_id: string;
  timezone: string;
};

type TruelarkScheduleAction = AirClientScheduleActionBase & {
  action_type: ScheduleActionType;
  scheduling_type: Extract<SchedulingType, 'TRUELARK_SCHEDULING'>;
};

export type AirClientScheduleAction =
  | WeaveDirectScheduleAction
  | WeaveAppointmentRequestAction
  | TruelarkScheduleAction;

type AirClientScheduleActionWithStartTime = AirClientScheduleAction & { start_time: string };
type AirClientScheduleActionWithAppointmentId = AirClientScheduleAction & { appointment_id: string };
type AirClientScheduleActionWithBookingSubmissionId = AirClientScheduleAction & { booking_submission_id: string };

export interface AirClientEvent {
  event_type: 'SCHEDULE_ACTION';
  schedule_action: AirClientScheduleAction;
}

export interface AirClientEventMessage {
  type: 'air_client_event';
  event: AirClientEvent;
}

const ROUTING_EVENT_TYPES: ReadonlySet<RoutingEventType> = new Set(['route', 'endCall', 'fatal', 'textHandoff']);
const SCHEDULE_ACTION_TYPES: ReadonlySet<ScheduleActionType> = new Set([
  'SCHEDULE',
  'RESCHEDULE',
  'CANCEL',
  'CONFIRM',
  'REQUEST',
]);
const SCHEDULE_ACTION_STATUSES: ReadonlySet<ScheduleActionStatus> = new Set(['SUCCEEDED', 'FAILED', 'UNKNOWN']);

export type VoiceAIRWebsocketMessage =
  | StreamAudioMessage
  | RouteActionMessage
  | TranscriptMessage
  | AirClientEventMessage;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const hasStartTime = (action: AirClientScheduleAction): action is AirClientScheduleActionWithStartTime =>
  'start_time' in action;

export const hasAppointmentId = (action: AirClientScheduleAction): action is AirClientScheduleActionWithAppointmentId =>
  'appointment_id' in action;

export const hasBookingSubmissionId = (
  action: AirClientScheduleAction
): action is AirClientScheduleActionWithBookingSubmissionId => 'booking_submission_id' in action;

export const isStreamAudioMessage = (value: VoiceAIRWebsocketMessage): value is StreamAudioMessage => {
  if (!isRecord(value) || value.type !== 'streamAudio' || !isRecord(value.data)) {
    return false;
  }

  const audioDataType = value.data.audioDataType;
  return (
    typeof value.data.audioData === 'string' &&
    typeof value.data.sampleRate === 'number' &&
    (audioDataType === undefined || audioDataType === 'raw')
  );
};

export const isRouteActionMessage = (value: VoiceAIRWebsocketMessage): value is RouteActionMessage => {
  if (!isRecord(value) || typeof value.type !== 'string' || !ROUTING_EVENT_TYPES.has(value.type as RoutingEventType)) {
    return false;
  }

  if (!isRecord(value.data)) {
    return false;
  }

  return typeof value.data.reason === 'string' && typeof value.data.caller_intent === 'string';
};

export const isTranscriptMessage = (value: VoiceAIRWebsocketMessage): value is TranscriptMessage => {
  if (!isRecord(value) || !isRecord(value.transcript)) {
    return false;
  }

  return (
    (value.transcript.role === 'user' || value.transcript.role === 'assistant') &&
    typeof value.transcript.text === 'string'
  );
};

export const isAirClientEventMessage = (value: VoiceAIRWebsocketMessage): value is AirClientEventMessage => {
  if (!isRecord(value) || value.type !== 'air_client_event' || !isRecord(value.event)) {
    return false;
  }

  if (value.event.event_type !== 'SCHEDULE_ACTION' || !isRecord(value.event.schedule_action)) {
    return false;
  }

  const { action_type, action_status } = value.event.schedule_action as Record<string, unknown>;
  return (
    typeof action_type === 'string' &&
    SCHEDULE_ACTION_TYPES.has(action_type as ScheduleActionType) &&
    typeof action_status === 'string' &&
    SCHEDULE_ACTION_STATUSES.has(action_status as ScheduleActionStatus)
  );
};

export const getSucceededScheduleActionFromAirClientEvent = (
  message: AirClientEventMessage
): AirClientScheduleAction | null => {
  const { schedule_action } = message.event;
  return schedule_action.action_status === 'SUCCEEDED' ? schedule_action : null;
};
